import { TimesheetsDataTable } from '@/components/(timesheets_table)/components/TimesheeetsDataTable'
import { timesheetsTableColumns } from '@/components/(timesheets_table)/components/timesheetsTableColumns'
import { timesheetMetadataArrayFromListRows } from '@/components/(timesheets_table)/utils/timesheets-list-metadata'
import { DashboardHeader } from '@/components/dashboard/dashboard-header'
import { DashboardShell } from '@/components/dashboard/dashboard-shell'
import { useDebouncedValue } from '@/hooks/use-debounced-value'
import { useTRPC } from '@/trpc/react'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import type { ColumnFiltersState } from '@tanstack/react-table'
import { type } from 'arktype'
import { DateTime } from 'luxon'
import { toast } from 'sonner'

const timesheetStatusValues = ['draft', 'submitted', 'approved', 'rejected'] as const
type TimesheetStatusValue = (typeof timesheetStatusValues)[number]

type TimesheetsSearch = {
  page: number
  pageSize: number
  employee: string
  client: string
  status: string
  dateFrom: string
  dateTo: string
}

function searchToColumnFilters(search: TimesheetsSearch): ColumnFiltersState {
  const filters: ColumnFiltersState = []
  if (search.employee?.trim()) {
    filters.push({ id: 'employee', value: search.employee })
  }
  if (search.client?.trim()) {
    filters.push({ id: 'client', value: search.client })
  }
  const statusValues = search.status
    ? search.status
        .split(',')
        .map((s) => s.trim().toLowerCase())
        .filter((s): s is TimesheetStatusValue =>
          timesheetStatusValues.includes(s as TimesheetStatusValue),
        )
    : []
  if (statusValues.length > 0) {
    filters.push({ id: 'status', value: Array.from(new Set(statusValues)) })
  }
  // Persist partial date filter (from only, to only, or both) so the calendar displays
  // selected values. API call only fires when both are present (see dateFilter below).
  const fromValid =
    search.dateFrom?.trim() && DateTime.fromISO(search.dateFrom, { zone: 'utc' }).isValid
  const toValid = search.dateTo?.trim() && DateTime.fromISO(search.dateTo, { zone: 'utc' }).isValid
  if (fromValid || toValid) {
    filters.push({
      id: 'date',
      value: {
        from: fromValid ? new Date(search.dateFrom) : undefined,
        to: toValid ? new Date(search.dateTo) : undefined,
      },
    })
  }
  return filters
}

function columnFiltersToSearchParams(filters: ColumnFiltersState): Partial<TimesheetsSearch> {
  const employee = filters.find((f) => f.id === 'employee')?.value as string | undefined
  const client = filters.find((f) => f.id === 'client')?.value as string | undefined
  const status = filters.find((f) => f.id === 'status')?.value as string[] | undefined
  const date = filters.find((f) => f.id === 'date')?.value as
    | { from?: Date; to?: Date }
    | undefined

  return {
    employee: employee ?? '',
    client: client ?? '',
    status: Array.isArray(status) ? status.join(',') : '',
    dateFrom: date?.from ? DateTime.fromJSDate(date.from).toFormat('yyyy-MM-dd') : '',
    dateTo: date?.to ? DateTime.fromJSDate(date.to).toFormat('yyyy-MM-dd') : '',
  }
}

export const Route = createFileRoute('/dashboard/admin/timesheets/')({
  component: RouteComponent,
  validateSearch: type({
    page: 'number = 1',
    pageSize: 'number = 50',
    employee: 'string = ""',
    client: 'string = ""',
    status: 'string = ""',
    dateFrom: 'string = ""',
    dateTo: 'string = ""',
  }),
})

function RouteComponent() {
  const api = useTRPC()
  const search = Route.useSearch()
  const navigate = Route.useNavigate()
  const columnFilters = searchToColumnFilters(search)

  const page = Math.max(1, Math.trunc(search.page))
  const pageSize = Math.min(Math.max(1, Math.trunc(search.pageSize)), 50)

  const employeeNameFilter = columnFilters.find((f) => f.id === 'employee')?.value as
    | string
    | undefined
  const clientNameFilter = columnFilters.find((f) => f.id === 'client')?.value as
    | string
    | undefined
  const debouncedEmployeeName = useDebouncedValue(employeeNameFilter ?? '', 800)
  const debouncedClientName = useDebouncedValue(clientNameFilter ?? '', 800)
  const dateFilterRaw = columnFilters.find((f) => f.id === 'date')?.value as
    | { from?: Date; to?: Date }
    | undefined
  const statusFilterRaw = columnFilters.find((f) => f.id === 'status')?.value as
    | string[]
    | undefined

  let dateFilter: { from: Date; to: Date } | undefined = undefined
  if (dateFilterRaw?.from && dateFilterRaw.to) {
    const from = new Date(dateFilterRaw.from)
    const to = new Date(dateFilterRaw.to)
    const isFromValid = !Number.isNaN(from.getTime())
    const isToValid = !Number.isNaN(to.getTime())
    if (isFromValid && isToValid) {
      dateFilter = { from, to }
    } else {
      toast.error('Invalid date filter')
    }
  }

  const { data: timesheetsResult, isPending } = useQuery({
    ...api.timesheet.getTimesheetsForBusiness.queryOptions({
      page,
      pageSize,
      employeeName: debouncedEmployeeName ? debouncedEmployeeName : undefined,
      clientName: debouncedClientName ? debouncedClientName : undefined,
      date: dateFilter,
      status: statusFilterRaw,
    }),
  })

  const timesheetsMetadata = timesheetsResult
    ? timesheetMetadataArrayFromListRows(timesheetsResult.data)
    : undefined

  function handlePaginationChange(nextPagination: { page: number; pageSize: number }): void {
    const normalizedPageSize = Math.min(Math.max(1, Math.trunc(nextPagination.pageSize)), 50)
    const normalizedPage =
      normalizedPageSize !== pageSize ? 1 : Math.max(1, Math.trunc(nextPagination.page))

    navigate({
      search: (previousSearch) => ({
        ...previousSearch,
        page: normalizedPage,
        pageSize: normalizedPageSize,
      }),
    })
  }

  function handleColumnFiltersChange(
    nextColumnFiltersOrUpdater:
      | ColumnFiltersState
      | ((old: ColumnFiltersState) => ColumnFiltersState),
  ): void {
    const nextColumnFilters =
      typeof nextColumnFiltersOrUpdater === 'function'
        ? nextColumnFiltersOrUpdater(columnFilters)
        : nextColumnFiltersOrUpdater

    navigate({
      search: (previousSearch) => ({
        ...previousSearch,
        page: 1,
        ...columnFiltersToSearchParams(nextColumnFilters),
      }),
    })
  }

  const paginationPlaceholder = {
    page,
    pageSize,
    totalCount: 0,
    totalPages: 1,
    hasNextPage: false,
    hasPreviousPage: false,
  }

  return (
    <DashboardShell>
      <DashboardHeader heading="Timesheets" text="View and approve employee timesheets." />
      {isPending || !timesheetsResult ? (
        <TimesheetsDataTable
          data={[]}
          columns={timesheetsTableColumns}
          timesheetsMetadata={undefined}
          isTimesheetsMetadataPending={false}
          isLoading
          pagination={paginationPlaceholder}
          onPaginationChange={handlePaginationChange}
          columnFilters={columnFilters}
          onColumnFiltersChange={handleColumnFiltersChange}
        />
      ) : (
        <TimesheetsDataTable
          data={timesheetsResult.data}
          columns={timesheetsTableColumns}
          timesheetsMetadata={timesheetsMetadata}
          isTimesheetsMetadataPending={false}
          pagination={timesheetsResult.pagination}
          onPaginationChange={handlePaginationChange}
          columnFilters={columnFilters}
          onColumnFiltersChange={handleColumnFiltersChange}
        />
      )}
    </DashboardShell>
  )
}
